/**
 * Near-Me Business Schemas
 * ========================
 * Create / update / search request bodies + wire types for a business listing.
 *
 * Refine gotcha: `.partial()` on a `.refine()`d object throws at module load
 * in Zod v4, so the create body is split — `createBusinessBaseSchema` (plain
 * object, the source for `.partial()`) and `createBusinessSchema` (base +
 * refine). See the memory note feedback_zod4_partial_on_refined.
 */

import { z } from 'zod';
import type { NearMeDay, NearMeStatus, NearMeTag } from './enums.js';
import { NEAR_ME_DAY, NEAR_ME_STATUS, NEAR_ME_TAG } from './enums.js';
import type { GeoJsonPoint } from './helpers.js';
import {
  emptyStringToUndefined,
  geoJsonPointSchema,
  optionalEmailSchema,
  optionalPhoneSchema,
  optionalTrimmedString,
  optionalUrlSchema,
  phoneSchema,
  timeOfDaySchema,
} from './helpers.js';
import type { NearMeCategory, NearMeSubcategory } from './taxonomy.js';
import { isSubcategoryOfCategory, NEAR_ME_CATEGORY, NEAR_ME_SUBCATEGORY } from './taxonomy.js';

/** 24-char hex ObjectId (as a string, for area references). */
const objectIdSchema = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');

/** Coerces a comma-separated query string into a string array. */
const csvToArray = (value: unknown): unknown =>
  typeof value === 'string'
    ? value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : value;

/** Coerces "true"/"false" query strings into real booleans. */
const booleanQueryParam = z.preprocess((value) => {
  if (value === 'true' || value === true) return true;
  if (value === 'false' || value === false) return false;
  return undefined;
}, z.boolean().optional());

// ─── Sub-schemas ──────────────────────────────────────────────────────────

/**
 * A branch/outlet. Multi-branch collects address + geo only (not a separate
 * listing). The first branch in the array is treated as primary.
 */
export const branchSchema = z.object({
  label: optionalTrimmedString(80),
  address: z.string().trim().min(5).max(300),
  location: geoJsonPointSchema,
  locality: optionalTrimmedString(100),
  city: optionalTrimmedString(100),
  landmark: optionalTrimmedString(120),
  phone: optionalPhoneSchema,
  areaId: objectIdSchema.optional(),
});

/** One day's opening hours. `closed` days omit open/close. */
export const dayHoursSchema = z.object({
  day: z.enum(NEAR_ME_DAY),
  closed: z.boolean().default(false),
  open: timeOfDaySchema.optional(),
  close: timeOfDaySchema.optional(),
});

/** Business contact block. Primary phone is required. */
export const contactSchema = z.object({
  phone: phoneSchema,
  whatsapp: optionalPhoneSchema,
  email: optionalEmailSchema,
  website: optionalUrlSchema,
});

// ─── Create / Update bodies ─────────────────────────────────────────────────

/** Plain create body (no refine) — the source for `.partial()`. */
export const createBusinessBaseSchema = z.object({
  name: z.string().trim().min(3).max(120),
  category: z.enum(NEAR_ME_CATEGORY),
  subcategory: z.enum(NEAR_ME_SUBCATEGORY),
  tagline: optionalTrimmedString(160),
  description: z.preprocess(emptyStringToUndefined, z.string().trim().min(10).max(2000).optional()),
  tags: z.array(z.enum(NEAR_ME_TAG)).max(20).default([]),
  branches: z.array(branchSchema).min(1).max(10),
  contact: contactSchema,
  hours: z.array(dayHoursSchema).max(7).optional(),
  logo: optionalUrlSchema,
  coverImage: optionalUrlSchema,
  photos: z.array(z.string().trim().url()).max(10).default([]),
  establishedYear: z.number().int().min(1800).max(2100).optional(),
});

/** Create body — enforces subcategory belongs to the chosen category. */
export const createBusinessSchema = createBusinessBaseSchema.refine(
  (data) => isSubcategoryOfCategory(data.category, data.subcategory),
  { message: 'Subcategory does not belong to the selected category', path: ['subcategory'] },
);
export type CreateBusinessInput = z.infer<typeof createBusinessSchema>;

/** Update body — all fields optional; re-checks the pair only when both given. */
export const updateBusinessSchema = createBusinessBaseSchema
  .partial()
  .refine(
    (data) =>
      !data.category ||
      !data.subcategory ||
      isSubcategoryOfCategory(data.category, data.subcategory),
    { message: 'Subcategory does not belong to the selected category', path: ['subcategory'] },
  );
export type UpdateBusinessInput = z.infer<typeof updateBusinessSchema>;

// ─── Query bodies ────────────────────────────────────────────────────────────

/** Public discovery / search query. Radius defaults to 1km (the "near me" core). */
export const searchBusinessesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  q: optionalTrimmedString(100),
  category: z.enum(NEAR_ME_CATEGORY).optional(),
  subcategory: z.enum(NEAR_ME_SUBCATEGORY).optional(),
  tags: z.preprocess(csvToArray, z.array(z.enum(NEAR_ME_TAG)).optional()),
  lng: z.coerce.number().min(-180).max(180).optional(),
  lat: z.coerce.number().min(-90).max(90).optional(),
  radiusKm: z.coerce.number().min(0.5).max(100).default(1),
  openNow: booleanQueryParam,
  sortBy: z.enum(['distance', 'rating', 'newest', 'relevance']).default('distance'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type SearchBusinessesQuery = z.infer<typeof searchBusinessesQuerySchema>;

/** Owner's "my businesses" list query. */
export const myBusinessesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  status: z.enum(NEAR_ME_STATUS).optional(),
});
export type MyBusinessesQuery = z.infer<typeof myBusinessesQuerySchema>;

// ─── Wire types (response shapes; never validated client-side) ───────────────

export interface NearMeBranch {
  label?: string;
  address: string;
  location: GeoJsonPoint;
  locality?: string;
  city?: string;
  landmark?: string;
  phone?: string;
  areaId?: string;
}

export interface NearMeDayHours {
  day: NearMeDay;
  closed: boolean;
  open?: string;
  close?: string;
}

export interface NearMeContact {
  phone: string;
  whatsapp?: string;
  email?: string;
  website?: string;
}

/**
 * Full business entity as returned by detail / owner / search endpoints.
 * Matches the backend lean document shape (house `createdBy` + `creatorName`
 * convention); geo searches also carry `distanceKm`.
 */
export interface NearMeBusiness {
  _id: string;
  createdBy: string;
  creatorName: string;
  name: string;
  slug: string;
  category: NearMeCategory;
  subcategory: NearMeSubcategory;
  tagline?: string;
  description?: string;
  tags: NearMeTag[];
  branches: NearMeBranch[];
  contact: NearMeContact;
  hours?: NearMeDayHours[];
  logo?: string;
  coverImage?: string;
  photos: string[];
  establishedYear?: number;
  status: NearMeStatus;
  verified: boolean;
  /** Premium-plan featured placement — a discovery sort boost + a badge. */
  featured: boolean;
  ratingAvg: number;
  ratingCount: number;
  viewCount: number;
  leadCount: number;
  savedCount: number;
  /** Distance in km from the search origin, present only on geo searches. */
  distanceKm?: number;
  createdAt: string;
  updatedAt: string;
}

/** Paginated search envelope (mirrors every list endpoint). */
export interface NearMeBusinessSearchResult {
  items: NearMeBusiness[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
